import { useSyncExternalStore } from 'react';
import { applyMode, Mode } from '@cloudscape-design/global-styles';

/** Esquema de cor efetivo do admin, espelho do `prefers-color-scheme` do SO. */
export type ColorScheme = 'light' | 'dark';

const darkQuery: MediaQueryList | null =
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-color-scheme: dark)')
    : null;

/** Esquema atual pela media query (claro quando não há `matchMedia`). */
function currentScheme(): ColorScheme {
  return darkQuery?.matches ? 'dark' : 'light';
}

/**
 * Aplica o esquema de cor (claro/escuro) ao documento. O admin é 100%
 * Cloudscape: o tema é dirigido SÓ pelo `applyMode` do Cloudscape (o sistema
 * shadcn `.dark` foi removido junto com o Tailwind). O `<body>` em si
 * (login/áreas fora dos componentes) escurece via `@media
 * (prefers-color-scheme)` em index.css.
 */
function applyScheme(scheme: ColorScheme): void {
  const dark = scheme === 'dark';
  // Cloudscape: `applyMode` TEM que ir no <body>. O global-styles ativa o dark
  // via `html:has(body.awsui-dark-mode)` — aplicar no documentElement (html)
  // NÃO dispara o tema e o conteúdo interno fica branco no modo escuro
  // (verificado com Playwright: html=branco, body=escuro). Sprint 39.
  applyMode(dark ? Mode.Dark : Mode.Light, document.body);
}

/**
 * Bootstrap do tema (chamado uma vez em main.tsx). Segue a preferência do SO
 * e reage a mudanças em tempo real (modo noturno do sistema liga/desliga).
 *
 * Origem: sem issue — commit 5056c2f15, 2026-06-08 (modo escuro segue o SO).
 *
 * Usado por: `main.tsx`.
 *
 * Dicas: o listener nunca é removido de propósito — vive o app inteiro. Chamar
 * duas vezes duplica o listener (inofensivo, mas desnecessário).
 */
export function initColorScheme(): void {
  applyScheme(currentScheme());
  darkQuery?.addEventListener('change', (e: MediaQueryListEvent): void => {
    applyScheme(e.matches ? 'dark' : 'light');
  });
}

/** Hook reativo: 'dark' quando o SO está em modo noturno, senão 'light'. Usado pela logo do `routes/Login.tsx`. */
export function useColorScheme(): ColorScheme {
  return useSyncExternalStore(
    (onChange) => {
      darkQuery?.addEventListener('change', onChange);
      return (): void => {
        darkQuery?.removeEventListener('change', onChange);
      };
    },
    currentScheme,
    () => 'light',
  );
}
